import React from "react";
import { UseFormRegister, FieldErrors, UseFormSetValue, UseFormClearErrors } from "react-hook-form";
import Input from "../../../components/Input";
import SelectInput from "../../../components/Select";
import TextareaField from "../../../components/common/TextareaField";
import { OrderFormData, SHIPMENT_SERVICE_OPTIONS, PAYMENT_TYPE_OPTIONS, ORDER_SHIPMENT_TYPE_OPTIONS } from "../../../types/types";
import { IconPackage } from "@tabler/icons-react";
import NewinputField from "../../../components/NewInput";
import NewSelectInput from "../../../components/NewSelectInput";

interface ShipmentSectionProps {
    register: UseFormRegister<OrderFormData>;
    errors: FieldErrors<OrderFormData>;
    setValue: UseFormSetValue<OrderFormData>;
    watch: any;
    clearErrors: UseFormClearErrors<OrderFormData>;
    profile?: any;
    isSidebar?: boolean;
    isSubmitted?: boolean;
    disableWeight?: boolean;
    useFMOrder?: boolean;
    setUseFMOrder?: (val: boolean) => void;
    isBothAccess?: boolean;
}


const ShipmentSection: React.FC<ShipmentSectionProps> = ({
    register,
    errors,
    setValue,
    watch,
    clearErrors,
    profile,
    isSidebar = false,
    isSubmitted = false,
    disableWeight = false,
    useFMOrder = false,
    setUseFMOrder,
    isBothAccess = false,
}) => {
    if (isSidebar) {
        return (
          <div className="space-y-4">
            {/* Section header */}
            <div className="mb-2 flex items-center justify-between">
              <p className="text-[#212121]/80 uppercase text-xs font-bold tracking-wider">Shipment Details</p>
              {isBothAccess && (
                <button
                  type="button"
                  onClick={() => setUseFMOrder && setUseFMOrder(!useFMOrder)}
                  className={`flex items-center gap-1.5 px-2.5 py-0.5 rounded-full border text-xs font-bold transition-all ${
                    useFMOrder
                      ? "bg-[#FBD037] border-[#FBD037] text-[#212121]"
                      : "bg-white border-gray-300 text-gray-500 hover:border-[#FBD037]"
                  }`}
                >
                  <span className={`w-1.5 h-1.5 rounded-full ${useFMOrder ? "bg-[#212121]" : "bg-gray-400"}`} />
                  {useFMOrder ? "FM" : "LM"}
                </button>
              )}
            </div>
    
            <div className="grid grid-cols-2 gap-3 relative z-20">
              <NewinputField
                name="shipment.referenceNumber"
                type="text"
                title="Reference"
                register={register}
                error={errors.shipment?.referenceNumber}
                validation={{
                    required: "Required",
                    maxLength: {
                        value: 10,
                        message: "Max 10 chars",
                    },
                }}
                className="flex flex-col gap-1"
              />
    
              <NewinputField
                name="shipment.shipmentCost"
                type="text"
                title="Cost"
                register={register}
                error={errors.shipment?.shipmentCost}
                validation={{ required: "Required" }}
                className="flex flex-col gap-1"
              />
    
              <NewSelectInput
                name="shipment.paymentType"
                label="Payment"
                placeholder="Select type"
                className="flex flex-col gap-1"
                selectOptions={PAYMENT_TYPE_OPTIONS}
                clearErrors={clearErrors}
                register={register}
                value={watch("shipment.paymentType")}
                error={errors.shipment?.paymentType}
                setValue={(val: string) => { setValue("shipment.paymentType", val); if (val !== "COD") setValue("shipment.codAmount", ""); }}
                validation={{ required: "Required" }}
              />
    
              {watch("shipment.paymentType") === "COD" && (
                <NewinputField
                  name="shipment.codAmount"
                  type="number"
                  title="COD Amt"
                  register={register}
                  error={errors.shipment?.codAmount}
                  validation={{ required: "Required" }}
                  className="flex flex-col gap-1"
                />
              )}
    
              <NewinputField
                name="shipment.numberOfPieces"
                type="number"
                title="Pieces"
                register={register}
                error={isSubmitted ? errors.shipment?.numberOfPieces : undefined}
                validation={{
                    required: "Required",
                    min: { value: 1, message: "Must be at least 1" }
                }}
                className="flex flex-col gap-1"
              />
    
              <NewinputField
                name="shipment.totalWeight"
                type="number"
                title="Weight (kg)"
                register={register}
                error={errors.shipment?.totalWeight}
                disabled={disableWeight}
                validation={{
                  required: { value: !disableWeight, message: "Required" },
                  min: { value: 0.001, message: "Must be > 0" },
                  validate: (v: any) => disableWeight || parseFloat(v) > 0 || "Must be > 0",
                }}
                className="flex flex-col gap-1"
              />
    
              <NewSelectInput
                name="shipment.serviceType"
                label="Service"
                placeholder="Select service"
                className="flex flex-col gap-1"
                selectOptions={SHIPMENT_SERVICE_OPTIONS}
                clearErrors={clearErrors}
                register={register}
                validation={{ required: "Required" }}
                error={errors.shipment?.serviceType}
                value={watch("shipment.serviceType")}
                setValue={(val: string) => setValue("shipment.serviceType", val)}
              />
    
              <NewSelectInput
                name="shipment.shipmentType"
                label="Type"
                placeholder="Select"
                className="flex flex-col gap-1"
                selectOptions={ORDER_SHIPMENT_TYPE_OPTIONS}
                clearErrors={clearErrors}
                register={register}
                validation={{ required: "Required" }}
                error={errors.shipment?.shipmentType}
                value={watch("shipment.shipmentType")}
                setValue={(val: string) => setValue("shipment.shipmentType", val)}
              />
            </div>
    
            <NewinputField
              name="shipment.description"
              type="text"
              title="Description"
              register={register}
              error={errors.shipment?.description}
              validation={{ required: "Required" }}
              className="flex flex-col gap-1 relative z-10"
            />
          </div>
        );
    }

    return (
        <div className="grid grid-cols-6 gap-2">
            <Input
                name="shipment.referenceNumber"
                type="text"
                title="Reference"
                register={register}
                error={errors.shipment?.referenceNumber}
                validation={{
                    required: "Reference is required",
                    maxLength: {
                        value: 10,
                        message: "Reference cannot exceed 10 characters",
                    },
                }}
                className="col-span-3"
            />

            <Input
                name="shipment.shipmentCost"
                type="text"
                title="Shipment Cost"
                register={register}
                error={errors.shipment?.shipmentCost}
                validation={{ required: "Shipment cost is required" }}
                className="col-span-3"
            />

            <SelectInput
                name="shipment.paymentType"
                label="Payment Type"
                className="col-span-2"
                placeholder="Select Payment Type"
                selectOptions={PAYMENT_TYPE_OPTIONS}
                clearErrors={clearErrors}
                register={register}
                error={errors.shipment?.paymentType}
                value={watch('shipment.paymentType')}
                setValue={(val: string) => {
                    setValue('shipment.paymentType', val);
                    if (val !== 'COD') {
                        setValue('shipment.codAmount', '');
                    }
                }}
                validation={{ required: "Payment Type is required" }}
            />

            {watch('shipment.paymentType') === 'COD' && (
                <Input
                    name="shipment.codAmount"
                    type="number"
                    title="COD Amount"
                    register={register}
                    error={errors.shipment?.codAmount}
                    validation={{ required: "COD amount is required" }}
                    className="col-span-2"
                />
            )}

            <Input
                name="shipment.numberOfPieces"
                type="number"
                title="No of Pieces"
                register={register}
                error={isSubmitted ? errors.shipment?.numberOfPieces : undefined}
                validation={{
                    required: "No of pieces is required",
                    min: { value: 1, message: "Must be at least 1" }
                }}
                className="col-span-2"
            />

            <Input
                name="shipment.totalWeight"
                type="number"
                title="Total Weight (kg)"
                register={register}
                error={errors.shipment?.totalWeight}
                disabled={disableWeight}
                validation={{
                    required: { value: !disableWeight, message: "Total weight is required" },
                    pattern: {
                        value: /^\d*\.?\d*$/,
                        message: "Must be a valid number"
                    },
                    validate: (v: any) => disableWeight || parseFloat(v) > 0 || "Must be > 0",
                }}
                className="col-span-2"
            />

            <SelectInput
                name="shipment.serviceType"
                label="Service"
                className="col-span-2"
                placeholder="Select Service"
                selectOptions={SHIPMENT_SERVICE_OPTIONS}
                clearErrors={clearErrors}
                register={register}
                validation={{ required: "Service is required" }}
                value={watch('shipment.serviceType')}
                setValue={(val: string) => {
                    setValue('shipment.serviceType', val);
                }}
                error={errors.shipment?.serviceType}
            />

            <SelectInput
                name="shipment.shipmentType"
                label="Shipment Type"
                className="col-span-2"
                placeholder="Select Shipment Type"
                selectOptions={ORDER_SHIPMENT_TYPE_OPTIONS}
                clearErrors={clearErrors}
                register={register}
                validation={{ required: "Shipment type is required" }}
                value={watch('shipment.shipmentType')}
                setValue={(val: string) => {
                    setValue('shipment.shipmentType', val);
                }}
                error={errors.shipment?.shipmentType}
            />

            <TextareaField
                rows={2}
                className="col-span-6"
                label="Description"
                register={register}
                placeholder=""
                name="shipment.description"
                error={errors.shipment?.description}
                validation={{ required: "Description is required" }}
            />
        </div>
    );
};

export default ShipmentSection;